import { HttpClient } from '@angular/common/http';
import { CreateEffectOptions, effect, inject, Injectable, signal } from '@angular/core';

import { UnsubscribeService } from '@webgate/angular-azure-core';

import { Roles } from '../models/roles';
import { UserRoleProvider } from '../models/UserRoleProvider';

import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AccessService {
  httpClient: HttpClient = inject(HttpClient);
  unsubscribeService: UnsubscribeService = inject(UnsubscribeService);
  configService: ConfigService = inject(ConfigService);
  userRoleProvider = signal(new UserRoleProvider());
  isAdmin = signal(false);
  isGprmAuditor = signal(false);
  isGprmAssessmentCoordinator = signal(false);
  isGgpiaAssessmentCoordinator = signal(false);
  isGprmRead = signal(false);
  isGpiaAuditor = signal(false);
  isGpiaRead = signal(false);
  readOnly = signal(false);

  constructor() {
    this.initRoles();
    this.initializeEffect();
  }

  initRoles() {
    this.httpClient
      .get<UserRoleProvider>(this.getHost() + '/api/app/permissions')
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (userRoleProvider: UserRoleProvider) => {
          this.userRoleProvider.set(userRoleProvider);
        }
      });
  }

  initializeEffect() {
    const effectOptions: CreateEffectOptions = {
      allowSignalWrites: true
    };

    effect(() => {
      const userRoleProvider = this.userRoleProvider();
      if (userRoleProvider) {
        if (userRoleProvider.appRegistrationRoles.includes(Roles.IT_ADMIN)) {
          this.isAdmin.set(true);
          this.isGprmAuditor.set(true);
          this.isGprmAssessmentCoordinator.set(true);
          this.isGgpiaAssessmentCoordinator.set(true);
          this.isGpiaAuditor.set(true);
          this.isGprmRead.set(true);
          this.isGpiaRead.set(true);
        } else {
          this.isAdmin.set(userRoleProvider.appRegistrationRoles.includes(Roles.Owners));
          this.isGprmAuditor.set(userRoleProvider.appRegistrationRoles.includes(Roles.Authors_GPRMAUD));
          this.isGprmAssessmentCoordinator.set(userRoleProvider.appRegistrationRoles.includes(Roles.Authors_GPRMCOR));
          this.isGgpiaAssessmentCoordinator.set(userRoleProvider.appRegistrationRoles.includes(Roles.Authors_GPIACOR));
          this.isGpiaAuditor.set(userRoleProvider.appRegistrationRoles.includes(Roles.Authors_GPIAAUD));
          this.isGprmRead.set(
            this.hasOneOfAppRegistrationRoles([Roles.Readers_GPRM, Roles.Authors_GPRMAUD, Roles.Authors_GPRMCOR])
          );
          this.isGpiaRead.set(
            this.hasOneOfAppRegistrationRoles([
              Roles.Readers_GPIA_BGSAMEA,
              Roles.Readers_GPIA_BGASIA,
              Roles.Readers_GPIA_BGNA,
              Roles.Readers_GPIA_BGEU,
              Roles.Authors_GPIAAUD,
              Roles.Authors_GPIACOR
            ])
          );
        }
      }
    }, effectOptions);
  }

  /**
   * Checks if the current user has an Auditor role (AUD).
   * Only users with Auditor roles (GPRM or GPIA) or Admin are considered auditors.
   * Users with only AssessmentCoordinator roles (COR) are not auditors.
   */
  hasAuditorRole(): boolean {
    return this.isGprmAuditor() || this.isGpiaAuditor() || this.isAdmin();
  }

  private hasOneOfAppRegistrationRoles(roles: string[]): boolean {
    for (const role of roles) {
      if (this.userRoleProvider()?.appRegistrationRoles.includes(role)) {
        return true;
      }
    }
    return false;
  }

  private getHost(): string {
    return this.configService.getConfiguration().apiHost;
  }
}
